import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Clock, Navigation, Music } from 'lucide-react';
import { hoursOfOperation, restaurantInfo } from '../config/siteConfig';
import { getFutureBands } from '../utils/bandDateUtils';

const HoursSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  
  // Get the next band's day to show "Live Music!" on the correct day
  const getNextBandDay = () => {
    const futureBands = getFutureBands();
    if (!futureBands || futureBands.length === 0) return null;
    const nextBand = futureBands[0];
    if (!nextBand || nextBand.band === 'TBD') return null;
    
    // Parse day from date string (e.g., "Sat, Jan 24" -> "Saturday")
    const dayAbbr = nextBand.date.split(',')[0].trim().toLowerCase();
    const dayMap = {
      'sun': 'Sunday',
      'mon': 'Monday',
      'tue': 'Tuesday',
      'wed': 'Wednesday',
      'thu': 'Thursday',
      'fri': 'Friday',
      'sat': 'Saturday'
    };
    
    for (const [abbr, full] of Object.entries(dayMap)) {
      if (dayAbbr.startsWith(abbr)) return full;
    }
    return null;
  };
  
  const nextBandDay = getNextBandDay();

  return (
    <section id="hours" ref={sectionRef} className="py-20 lg:py-28 bg-[#2C2416] relative overflow-hidden">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />
      
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4A574] to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#D4A574] font-semibold tracking-widest text-sm uppercase">Visit Us</span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FDF6E9] tracking-wide leading-[0.95] mt-3">
            COME HANG OUT
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Hours */}
          <div className={`transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="bg-[#FDF6E9] p-5 sm:p-8 border-4 border-[#D4A574]">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-[#C9A227]" />
                <h3 className="font-display text-2xl text-[#2C2416] tracking-wide">HOURS</h3>
              </div>
              
              <div className="space-y-2">
                {hoursOfOperation.map((item, i) => {
                  const isToday = item.day === today;
                  const hasLiveMusic = item.day === nextBandDay;
                  
                  return (
                    <div
                      key={i}
                      className={`flex flex-wrap gap-y-1 items-center justify-between py-3 px-4 rounded-lg transition-all ${
                        isToday
                          ? 'bg-[#C9A227] text-[#1a1612] shadow-lg ring-2 ring-[#C9A227] ring-offset-2 ring-offset-[#FDF6E9]'
                          : 'hover:bg-[#2C2416]/5 hover:translate-x-1'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {isToday && (
                          <span className="w-2.5 h-2.5 bg-[#1a1612] rounded-full animate-pulse shadow-sm" />
                        )}
                        <span className={`font-semibold ${isToday ? 'text-[#1a1612]' : 'text-[#2C2416]'}`}>
                          {item.day}
                          {isToday && <span className="ml-2 text-xs font-bold bg-[#1a1612]/15 px-2 py-0.5 rounded">TODAY</span>}
                        </span>
                        {hasLiveMusic && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${
                            isToday ? 'bg-[#1a1612]/15 text-[#1a1612]' : 'bg-[#C9A227] text-white'
                          }`}>
                            <Music className="w-3 h-3" />
                            Live Music!
                          </span>
                        )}
                        {item.special && !hasLiveMusic && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            isToday ? 'bg-[#1a1612]/15 text-[#1a1612]' : 'bg-[#C9A227]/15 text-[#8a6f1c] border border-[#C9A227]/30'
                          }`}>
                            {item.special}
                          </span>
                        )}
                      </div>
                      <span className={`font-medium ${item.closed ? 'text-[#2C2416]/30' : isToday ? 'text-[#1a1612]' : 'text-[#2C2416]/70'}`}>
                        {item.time}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Location & Contact */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="bg-[#FDF6E9] p-5 sm:p-8 border-4 border-[#D4A574] h-full">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-[#C9A227]" />
                <h3 className="font-display text-2xl text-[#2C2416] tracking-wide">LOCATION</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xl font-semibold text-[#2C2416]">{restaurantInfo.address}</p>
                  <p className="text-[#2C2416]/70">{restaurantInfo.city}, {restaurantInfo.state} {restaurantInfo.zip}</p>
                  <p className="text-[#C9A227] text-sm font-semibold mt-1">📍 {restaurantInfo.plaza}</p>
                </div>

                <a href={`tel:${restaurantInfo.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-[#2C2416] hover:text-[#C9A227] transition-colors group">
                  <Phone className="w-5 h-5" />
                  <span className="text-2xl font-display tracking-wide group-hover:underline">{restaurantInfo.phone}</span>
                </a>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <a href={`tel:${restaurantInfo.phone.replace(/[^0-9]/g, '')}`} className="btn-retro flex-1 text-center">
                    CALL NOW
                  </a>
                  <a
                    href={restaurantInfo.directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-4 border-3 border-[#2C2416] text-[#2C2416] font-display text-lg tracking-wide hover:bg-[#2C2416] hover:text-[#FDF6E9] transition-all"
                    style={{ borderWidth: '3px' }}
                  >
                    <Navigation className="w-5 h-5" />
                    DIRECTIONS
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-14 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-[#FDF6E9]/60 mb-4">Ready to grab a bite?</p>
          <a href={restaurantInfo.orderOnlineUrl} target="_blank" rel="noopener noreferrer" className="btn-retro text-xl px-12 py-5 animate-pulse-glow inline-block">
            ORDER ONLINE
          </a>
        </div>
      </div>
    </section>
  );
};

export default HoursSection;
